import React, {useContext, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Typography from "@material-ui/core/Typography";
import {Slider} from "@material-ui/lab";
import NGLContext from "../NGLContext";
import {ColormakerRegistry} from "ngl"
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from "@material-ui/core/Grid";
import Box from '@material-ui/core/Box';
import VisibilityOffOutlinedIcon
    from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import DeleteOutlineOutlinedIcon
    from '@material-ui/icons/DeleteOutlineOutlined';
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";


const useStyles = makeStyles(theme => ({
        sliderRoot: {
            width: 300,
        },
        slider: {
            padding: '22px 0px',
        },
        textField: {
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            width: 200,
        },
        menu: {
            width: 200,
        },
        formControl: {
            margin: theme.spacing(1),
            minWidth:
                120,
        },
        formContainer: {
            display: 'flex',
            flexWrap: 'wrap',
        },
    }))
;


// TODO: Since it seems there is no way to access
//  component.parameters.parameter.options, we need to use object from
//  ngl/global ...
const nglRepresentationInputs = {
    general: [
        {
            name: "visible",
            label: "View/Hide",
            type: "boolean",
            iconOn: <VisibilityOutlinedIcon/>,
            iconOff: <VisibilityOffOutlinedIcon/>,
            help: "",
            default: true,
            action: (stage, repr, value) => {
                repr.setVisibility(value)
            },
        },
        {
            name: "delete",
            label: "Delete",
            type: "button",
            icon: <DeleteOutlineOutlinedIcon/>,
            help: "",
            action: (stage, repr) => {
                repr.dispose()
            },
        },
    ],
    color: [{
        name: "colorScheme",
        label: "Color scheme",
        type: "select",
        help: "",
        action: (stage, repr, value) => {
            repr.setParameters({colorScheme: value})
        },
        options: Object.keys(ColormakerRegistry.getSchemes()).map(key => ({
            value: key,
            label: key
        })),
    },
        {
            name: "colorScale",
            label: "Color scale",
            type: "select",
            help: "",
            action: (stage, repr, value) => {
                repr.setParameters({colorScale: value})
            },
            options: Object.keys(ColormakerRegistry.getScales()).map(key => ({
                value: key,
                label: key
            })),
        },
        {
            name: "colorMode",
            label: "Color mode",
            type: "select",
            help: "",
            action: (stage, repr, value) => {
                repr.setParameters({colorMode: value})
            },
            options: Object.keys(ColormakerRegistry.getModes()).map(key => ({
                value: key,
                label: key
            })),
        },],
};


function RepresentationSelect({representation, representationInput}) {
    const classes = useStyles();
    const context = useContext(NGLContext);
    const reprName = representation ? representation.getParameters()[representationInput.name] : "";
    const reprLabel = representation ? representationInput.label : "";
    const reprId = "standard-representation-select-" + reprName;
    const [value, setValue] = useState(reprName);
    const handleChange = event => {
        representationInput.action(context.stage, representation, event.target.value);
        setValue(event.target.value);
    };
    return <React.Fragment>
        {representationInput &&
        <FormControl className={classes.formControl}>
            <InputLabel htmlFor={reprId}>{reprLabel}</InputLabel>
            <Select
                id={reprId}
                label="Select"
                // className={classes.textField}
                value={value}
                inputProps={{
                    id: reprId,
                }}
                onChange={handleChange}
            >
                {representationInput && representationInput.options.map((option, index) => (
                    <MenuItem key={reprId + "-" + index} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>{representationInput.help}</FormHelperText>
        </FormControl>
        }
    </React.Fragment>
};


function SwitchIcon({toggle, iconOn, iconOff}) {
    return <React.Fragment>
        {toggle ?
            iconOn : iconOff
        }
    </React.Fragment>
}


function StageRepresentationButton({representation, representationInput, switchFlag}) {
    const classes = useStyles();
    const context = useContext(NGLContext);
    const reprName = representation ? representation.getParameters()[representationInput.name] : "";
    const reprLabel = representation ? representationInput.label : "";
    const reprId = "standard-representation-select-" + reprName;
    const [toggle, setToggle] = useState(true);
    useEffect(() => {
        if (context.stage && switchFlag) {
            representationInput.action(context.stage, representation, toggle)
        } else if (context.stage && !switchFlag && !toggle) {
            representationInput.action(context.stage, representation)
        }
    }, [toggle]);
    const handleChange = () => {
        setToggle(prevState => !prevState);
    };
    return <React.Fragment>
        {representationInput &&
        <Tooltip title={representationInput.label}>
            <IconButton
                aria-label={representationInput.label}
                size="medium"
                className={classes.button}
                onClick={handleChange}
            >{switchFlag ?
                <SwitchIcon
                    toggle={toggle}
                    iconOn={representationInput.iconOn}
                    iconOff={representationInput.iconOff}
                /> : representationInput.icon
            }
            </IconButton>
        </Tooltip>
        }
    </React.Fragment>
};


function IsoSlider(props) {
    const classes = useStyles();
    const context = useContext(NGLContext);
    const representation = props.representation;
    const [reprIsoLevel, setReprIsoLevel] = useState(50);
    useEffect(() => {
        if (context.stage) {
            representation.setParameters({isolevel: reprIsoLevel});
        }
    }, [reprIsoLevel]);
    return <div className={classes.sliderRoot}>
        <Typography id="label">{reprIsoLevel}</Typography>
        < Slider
            classes={{container: classes.slider}}
            value={reprIsoLevel}
            min={0}
            max={2}
            aria-labelledby="label"
            onChange={() => setReprIsoLevel}
        />
    </div>
}

function RepresentationTab(props) {
    const representation = props.representation ? props.representation : null;
    const reprType = representation ? representation.getType() : "";
    const findInputType = (input, section, type) => (input[section].filter(input => input.type === type));
    const reprInputSections = Object.keys(nglRepresentationInputs);
    return <Box p={3}>
        <form
            noValidate
            autoComplete="off">
            {
                reprInputSections.map((section, sectionIndex) => (
                    <React.Fragment key={sectionIndex}>
                        <Typography variant="h6" gutterBottom>
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                        </Typography>
                        < Grid container>
                            {
                                reprType && findInputType(nglRepresentationInputs, section, "boolean").map((input, index) => (
                                    <StageRepresentationButton
                                        key={"repr-switch-" + section + "-" + index}
                                        representation={representation}
                                        representationInput={input}
                                        switchFlag={true}
                                    />
                                ))
                            }
                            {
                                reprType && findInputType(nglRepresentationInputs, section, "button").map((input, index) => (
                                    <StageRepresentationButton
                                        key={"repr-button-" + section + "-" + index}
                                        representation={representation}
                                        representationInput={input}
                                        switchFlag={false}
                                    />
                                ))
                            }
                            {
                                reprType && findInputType(nglRepresentationInputs, section, "select").map((input, index) => (
                                    <Grid
                                        item
                                        xs
                                        key={"repr-select-" + section + "-" + index}
                                    >
                                        <RepresentationSelect
                                            representation={representation}
                                            representationInput={input}
                                        />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    </React.Fragment>
                ))
            }

        </form>

        {reprType === 'surface' &&
        <IsoSlider representation={representation}/>
        }
    </Box>
}

export default RepresentationTab;